import { useMutation } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { getErrorMessage } from "@/lib/error-message";

interface FeedbackPayload {
  category: string;
  description: string;
  priority: string;
  screenshotBlob: Blob | null;
  extraFile: File | null;
  target?: string | null;
}

interface FeedbackResult {
  attachmentFailed: boolean;
  attachmentError: string | null;
}

export function useFeedbackSubmit() {
  return useMutation<FeedbackResult, Error, FeedbackPayload>({
    mutationFn: async (payload: FeedbackPayload): Promise<FeedbackResult> => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const ts = Date.now();
      let screenshotPath: string | null = null;
      let extraScreenshotPath: string | null = null;
      let attachmentFailed = false;
      let attachmentError: string | null = null;

      // Image uploads are best-effort: a failed upload must NOT discard the
      // written feedback (the text is the important part). We record the
      // failure so the UI can tell the user their image didn't attach.
      // try/catch (not just the returned { error }) so even a thrown network
      // failure can't propagate past the insert and lose the written feedback.
      if (payload.screenshotBlob) {
        try {
          const path = `${user.id}/${ts}-screenshot.png`;
          const { error } = await supabase.storage
            .from("feedback-screenshots")
            .upload(path, payload.screenshotBlob, { contentType: "image/png" });
          if (error) throw error;
          screenshotPath = path;
        } catch (e) {
          console.warn("[feedback] auto-screenshot upload failed", getErrorMessage(e, String(e)));
        }
      }

      if (payload.extraFile) {
        try {
          const ext = payload.extraFile.name.split(".").pop() || "png";
          const path = `${user.id}/${ts}-extra.${ext}`;
          const { error } = await supabase.storage
            .from("feedback-screenshots")
            .upload(path, payload.extraFile, { contentType: payload.extraFile.type });
          if (error) throw error;
          extraScreenshotPath = path;
        } catch (e) {
          console.warn("[feedback] attachment upload failed", getErrorMessage(e, String(e)));
          attachmentFailed = true;
          attachmentError = getErrorMessage(e, "") || null;
        }
      }

      const systemInfo: Record<string, string> = {
        viewport: `${window.innerWidth}x${window.innerHeight}`,
        userAgent: navigator.userAgent,
      };
      // Cap the free-text target so a huge string can't bloat the jsonb row.
      if (payload.target) systemInfo.target = payload.target.slice(0, 2000);

      const { error: insertError } = await supabase.from("user_feedback").insert({
        user_id: user.id,
        user_email: user.email,
        category: payload.category,
        description: payload.description,
        priority: payload.priority,
        page_url: window.location.pathname,
        screenshot_path: screenshotPath,
        extra_screenshot_path: extraScreenshotPath,
        system_info: systemInfo,
      });

      if (insertError) throw insertError;

      return { attachmentFailed, attachmentError };
    },
    onSuccess: (result) => {
      if (result.attachmentFailed) {
        const reason = result.attachmentError ? ` (${result.attachmentError})` : "";
        toast.warning(`Submitted — but your image couldn't be attached${reason}. You can reply with it directly.`);
      } else {
        toast.success("Submitted — thank you!");
      }
    },
    onError: (err: Error) => {
      toast.error("Failed to submit feedback: " + err.message);
    },
  });
}
